import { useMemo } from 'react';
import { SearchDropdownBase, type FieldBehavior, type BehaviorAPI } from './SearchDropdownBase';
import { useSelector } from 'react-redux';
import { IAppState } from '../core/redux/IAppState';
import { getResource } from '../language/resources/getResource';

export type IdListSearchDropdownProps = {
  label: string;
  value?: string | null | undefined;
  defaultValue?: string;
  onChange?: (value: string) => void;

  /** Cap for how many IDs the user is allowed to enter */
  maxIds?: number;
  onValidIdsChange?: (ids: number[]) => void;

  onSearch?: (text: string) => void;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;

  controlWidth?: number | string;
  controlMaxWidth?: number | string;
  controlHeight?: number | string;
  iconSize?: number | string;

  panelMinWidth?: number | string;
  panelMaxWidth?: number | string;
  panelMaxHeight?: number;

  /** Shown height for editable content (converted to CSS from rows & line-height) */
  rows?: number;
  /** When false: fixed height = rows; when true/undefined set min-height = rows and it grows */
  autoGrow?: boolean;

  showToolbarClear?: boolean;
  clearLabel?: string;

  maxChars?: number;
};

export function IdListSearchDropdown({
  label,
  value,
  defaultValue,
  onChange,
  maxIds,
  onValidIdsChange,
  onSearch,
  searchPlaceholder,
  className,
  disabled,
  onOpenChange,
  controlWidth,
  controlMaxWidth,
  controlHeight = 36,
  iconSize,
  panelMinWidth = 560,
  panelMaxWidth = 720,
  panelMaxHeight = 320,
  rows = 3,
  autoGrow = true,
  showToolbarClear = true,
  clearLabel = 'Clear',
  maxChars,
}: IdListSearchDropdownProps) {
  const resources = useSelector((state: IAppState) => state.locale);

  const behavior: FieldBehavior<number[]> = useMemo(() => {
    // Allowed while typing: digits, comma, space
    const DISALLOWED_TYPING = /[^0-9, ]/;

    const stripSpaces = (s: string) => (s ?? '').replace(/\s+/g, '');
    const MIN = 0;
    const MAX = 2147483647;
    const MAX_STR = '2147483647'; // 10 digits

    /** Tokenize by comma */
    const tokenizeByComma = (raw: string) => {
      const spans: { start: number; end: number; text: string }[] = [];
      let i = 0,
        start = 0;
      while (i <= raw.length) {
        if (i === raw.length || raw[i] === ',') {
          spans.push({ start, end: i, text: raw.slice(start, i) });
          start = i + 1;
        }
        i++;
      }
      return spans;
    };

    /** Trim whitespace */
    const trimSpan = (raw: string, span: { start: number; end: number }) => {
      let s = span.start,
        e = span.end;
      while (s < e && /\s/.test(raw[s]!)) s++;
      while (e > s && /\s/.test(raw[e - 1]!)) e--;
      return { start: s, end: e };
    };

    /** Convert to ints (ignore empty tokens); */
    const toInts = (raw: string): number[] => {
      const out: number[] = [];
      for (const t of tokenizeByComma(raw)) {
        const trimmed = trimSpan(raw, t);
        const token = stripSpaces(raw.slice(trimmed.start, trimmed.end));
        if (!token) continue;
        if (!/^\d+$/.test(token)) return [];
        const n = Number(token);
        if (!Number.isSafeInteger(n)) return [];
        if (n < MIN || n > MAX) return [];
        out.push(n);
      }
      return out;
    };

    /**
     * For a numeric token (digits only), compute the index where overflow begins
     *   - ignore leading zeros for numeric comparison,
     *   - if significant length > 10 → overflow starts after the 10th significant digit,
     *   - if significant length = 10 → compare against "2147483647" and highlight from
     *     the first position where token digit exceeds the max digit,
     *   - otherwise → no overflow.
     *
     * Returns the starting raw index (inclusive) or null if no overflow.
     */
    const findOverflowStartRawIndex = (
      raw: string,
      trimmed: { start: number; end: number },
      digits: string
    ): number | null => {
      const lzMatch = digits.match(/^0+/);
      const leadingZeros = lzMatch ? lzMatch[0].length : 0;
      const significantLength = digits.length - leadingZeros;

      if (significantLength === 0) return null; // "0" / all zeros

      if (significantLength > 10) {
        const overflowPosInTrimmed = leadingZeros + 10;
        return trimmed.start + overflowPosInTrimmed;
      }
      if (significantLength < 10) return null;

      for (let i = 0; i < 10; i++) {
        const d = digits[leadingZeros + i]!;
        const m = MAX_STR[i]!;
        if (d > m) return trimmed.start + leadingZeros + i;
        if (d < m) return null;
      }
      return null; // exactly MAX
    };

    /**
     * Track where the user last edited to pick the most relevant error to surface.
     * With contentEditable there is no selectionStart; we fall back to the end.
     */
    let lastEditIndex = 0;

    const getSelectionInCE = (api: BehaviorAPI) => {
      const el = api.fieldRef.current as HTMLDivElement | null;
      if (!el) return { start: (api.text ?? '').length, end: (api.text ?? '').length };
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return { start: (api.text ?? '').length, end: (api.text ?? '').length };
      const range = sel.getRangeAt(0);
      if (!el.contains(range.startContainer)) return { start: (api.text ?? '').length, end: (api.text ?? '').length };
      const preRange = document.createRange();
      preRange.selectNodeContents(el);
      preRange.setEnd(range.startContainer, range.startOffset);
      const start = preRange.toString().length;
      const preRange2 = document.createRange();
      preRange2.selectNodeContents(el);
      preRange2.setEnd(range.endContainer, range.endOffset);
      const end = preRange2.toString().length;
      return { start, end };
    };

    const beforeInput = (e: any, api: BehaviorAPI) => {
      const native = (e?.nativeEvent as InputEvent) ?? undefined;
      const inputType = native?.inputType ?? (e?.inputType ?? '');
      const data: string | null = e?.data ?? null;

      // Record last edit (caret start)
      const el = api.fieldRef.current as HTMLTextAreaElement | HTMLInputElement | HTMLDivElement | null;
      if (el && typeof (el as any).selectionStart === 'number') {
        lastEditIndex = (el as any).selectionStart as number;
      } else {
        const rng = getSelectionInCE(api);
        lastEditIndex = rng.start;
      }

      // Allow paste/drop; validator will handle after change
      const isPasteOrDrop =
        inputType === 'insertFromPaste' ||
        inputType === 'insertFromDrop' ||
        (typeof data === 'string' && data.length > 1);
      if (isPasteOrDrop) return;

      // Block disallowed characters
      if (typeof data === 'string' && DISALLOWED_TYPING.test(data)) {
        e.preventDefault();
        api.setKeyError(getResource(resources, 'ACCESS_OVERVIEW_DIGITS_COMMAS_SPACES_ERROR'));
        return;
      }

      if (typeof maxChars === 'number' && maxChars >= 0) {
        const ceSel =
          el && typeof (el as any).selectionStart === 'number'
            ? { start: (el as any).selectionStart as number, end: (el as any).selectionEnd as number }
            : getSelectionInCE(api);
        const cur = (api.text ?? '');
        const incoming = data ?? '';
        const nextLen = cur.length - (ceSel.end - ceSel.start) + incoming.length;
        if (nextLen > maxChars) {
          e.preventDefault();
          return;
        }
      }
    };

    const onPaste = (e: React.ClipboardEvent<any>, api: BehaviorAPI) => {
      const el = api.fieldRef.current as HTMLTextAreaElement | HTMLInputElement | HTMLDivElement | null;
      if (el && typeof (el as any).selectionStart === 'number') {
        lastEditIndex = (el as any).selectionStart as number;
      } else {
        const rng = getSelectionInCE(api);
        lastEditIndex = rng.start;
      }
      if (typeof maxChars === 'number' && maxChars >= 0) {
        const textData = e.clipboardData.getData('text');
        const cur = (api.text ?? '');
        const ceSel =
          el && typeof (el as any).selectionStart === 'number'
            ? { start: (el as any).selectionStart as number, end: (el as any).selectionEnd as number }
            : getSelectionInCE(api);
        const remaining = Math.max(0, maxChars - (cur.length - (ceSel.end - ceSel.start)));
        if (remaining <= 0) {
          e.preventDefault();
          return;
        }
        if (textData.length > remaining) {
          e.preventDefault();
          const insert = textData.slice(0, remaining);
          document.execCommand('insertText', false, insert);
        }
      }
    };

    const onDrop = undefined;

    const normalizeOnClose = (raw: string) => {
      // Remove whitespace
      const noWs = (raw ?? '').replace(/\s+/g, '');
      // Collapse multi-commas
      const collapse = noWs.replace(/,{2,}/g, ',');
      // Trim edge commas
      let trimmed = collapse.replace(/^,+/g, '').replace(/,+$/g, '');
      if (typeof maxChars === 'number' && maxChars >= 0 && trimmed.length > maxChars) {
        trimmed = trimmed.slice(0, maxChars);
      }
      if (!trimmed) return '';
      // Deduplicate, enforce bounds, cap maxIntegers
      const parts = trimmed.split(',').filter(Boolean);
      const seen = new Set<number>();
      const out: number[] = [];
      for (const p of parts) {
        if (!/^\d+$/.test(p)) continue;
        const n = Number(p);
        if (!Number.isSafeInteger(n)) continue;
        if (n < MIN || n > MAX) continue;
        if (!seen.has(n)) {
          seen.add(n);
          out.push(n);
        }
        if (typeof maxIds === 'number' && out.length >= maxIds) break;
      }
      let joined = out.join(',');
      if (typeof maxChars === 'number' && maxChars >= 0 && joined.length > maxChars) {
        joined = joined.slice(0, maxChars);
      }
      return joined;
    };

    /**
     * Build invalid ranges:
     *  - Overflow => highlight from first overflow digit to end of token.
     *  - MaxIds => highlight the entire token that exceeds the cap.
     *  - All other errors => message only (no overlay).
     */
    const validate = (raw: string) => {
      const cleaned = stripSpaces(raw);

      if (!cleaned) {
        return { error: null, validPayload: [] as number[], invalidRanges: [] as any[] };
      }

      type Invalid = { start: number; end: number; msg: string; kind: 'overflow' | 'other' };
      const invalids: Invalid[] = [];
      const overlayRanges: { start: number; end: number }[] = [];

      let validCount = 0;
      const tokenSpans = tokenizeByComma(raw);

      for (const t of tokenSpans) {
        const trimmed = trimSpan(raw, t);
        const tokenRaw = raw.slice(trimmed.start, trimmed.end);
        const token = stripSpaces(tokenRaw);

        if (!token) continue; // ignore empty tokens

        // Non-digit token => general error (no overlay)
        if (!/^\d+$/.test(token)) {
          invalids.push({
            start: trimmed.start,
            end: trimmed.end,
            msg: getResource(resources, 'ACCESS_OVERVIEW_INVALID_ID_LIST_ERROR'),
            kind: 'other',
          });
          continue;
        }

        // Numeric overflow check
        const overflowStart = findOverflowStartRawIndex(raw, trimmed, token);
        if (overflowStart != null) {
          invalids.push({
            start: trimmed.start,
            end: trimmed.end,
            msg: getResource(resources, 'ACCESS_OVERVIEW_INVALID_ID_ERROR'),
            kind: 'overflow',
          });
          overlayRanges.push({ start: overflowStart, end: trimmed.end });
          continue;
        }

        const n = Number(token);
        if (!Number.isSafeInteger(n) || n < MIN || n > MAX) {
          invalids.push({
            start: trimmed.start,
            end: trimmed.end,
            msg: getResource(resources, 'ACCESS_OVERVIEW_INVALID_ID_ERROR'),
            kind: 'other',
          });
          continue;
        }

        // Count only valid tokens towards maxIds
        validCount++;

        // Respect maxIds in live validation
        if (typeof maxIds === 'number' && validCount > maxIds) {
          invalids.push({
            start: trimmed.start,
            end: trimmed.end,
            msg: getResource(resources, 'ACCESS_OVERVIEW_MAX_IDS_ERROR', maxIds),
            kind: 'other',
          });
          // Highlight whole token that exceeds the cap so CE paints it red
          overlayRanges.push({ start: trimmed.start, end: trimmed.end });
          continue;
        }
      }

      if (invalids.length > 0) {
        let chosenIdx = invalids.findIndex(r => lastEditIndex >= r.start && lastEditIndex < r.end);
        if (chosenIdx < 0) {
          const center = (r: Invalid) => (r.start + r.end) / 2;
          let best = 0;
          let bestDist = Math.abs(center(invalids[0]) - lastEditIndex);
          for (let i = 1; i < invalids.length; i++) {
            const d = Math.abs(center(invalids[i]) - lastEditIndex);
            if (d < bestDist) {
              best = i;
              bestDist = d;
            }
          }
          chosenIdx = best;
        }
        return {
          error: invalids[chosenIdx].msg,
          invalidRanges: overlayRanges,
        };
      }

      // All good – compute valid payload
      const ints = toInts(raw);
      return { error: null, validPayload: ints, invalidRanges: [] as any[] };
    };

    const behavior: FieldBehavior<number[]> = {
      // NOTE: 'textarea' is multiline (ContentEditable)
      fieldKind: 'textarea',
      inputMode: undefined,
      maxLengthAttr: undefined,

      sanitizeOnChange: undefined,
      onPaste,
      onDrop,
      finalizeOnBlur: undefined,

      beforeInput,

      onKeyDown: (e, api) => {
        // Update last edit position (input) or fallback (CE)
        const element = api.fieldRef.current as HTMLTextAreaElement | HTMLInputElement | null;
        if (element && typeof (element as any).selectionStart === 'number') {
          (lastEditIndex as unknown as number) = (element as any).selectionStart as number;
        } else {
          (lastEditIndex as unknown as number) = (api.text ?? '').length;
        }

        const composing = Boolean((e.nativeEvent as any)?.isComposing);
        if (composing) return;

        const allowed = new Set([
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Home',
          'End',
          'Tab',
          'Enter',
        ]);
        if (allowed.has(e.key) || e.ctrlKey || e.metaKey) return;

        if (e.key && e.key.length === 1 && !/[0-9, ]/.test(e.key)) {
          e.preventDefault();
          api.setKeyError(getResource(resources, 'ACCESS_OVERVIEW_DIGITS_COMMAS_SPACES_ERROR'));
        }
      },

      normalizeOnClose,
      validate,
      caretToEndOnOpen: true,
    };

    return behavior;
  }, [maxIds, maxChars, resources]);

  return (
    <SearchDropdownBase<number[]>
      label={label}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      searchPlaceholder={searchPlaceholder}
      className={className}
      disabled={disabled}
      onOpenChange={onOpenChange}
      controlWidth={controlWidth}
      controlMaxWidth={controlMaxWidth}
      controlHeight={controlHeight}
      iconSize={iconSize}
      panelMinWidth={panelMinWidth}
      panelMaxWidth={panelMaxWidth}
      panelMaxHeight={panelMaxHeight}
      rows={rows}
      autoGrow={autoGrow}
      showToolbarClear={showToolbarClear}
      clearLabel={clearLabel}
      behavior={behavior}
      onValidPayload={onValidIdsChange}
      onSearch={onSearch}
      closeOnEnter={true}
    />
  );
}
